import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { isTrackingEnabled, hasAnalyticsConsent, subscribeToConsent } from './tracking';
import { setMapboxAccessToken } from './mapboxLoader';

const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics;

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // GA4 is a consent-gated tag: it may only load on the production host, and
  // only once the visitor has accepted statistics cookies. Both answers come
  // from the tag loader in public/index.html, which runs before this component
  // and re-checks on every Cookiebot decision. Re-render on that decision so a
  // visitor who accepts mid-visit still gets the tag without a page reload.
  // Note this deliberately keeps GA4 out of the server-rendered <head>:
  // isTrackingEnabled() is false without a `window`, so SSR emits nothing.
  const [analyticsAllowed, setAnalyticsAllowed] = useState(
    () => isTrackingEnabled() && hasAnalyticsConsent()
  );
  useEffect(() => {
    const recheck = () => setAnalyticsAllowed(isTrackingEnabled() && hasAnalyticsConsent());
    recheck();
    return subscribeToConsent(recheck);
  }, []);

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    //
    // This is the geocoding SDK (12 KB), which the Topbar location search needs
    // on every page. The map renderer, mapbox-gl (668 KB), used to be added
    // here as well — on every page, whether or not a map was ever drawn. It is
    // now fetched by util/mapboxLoader.js when a map component mounts.
    // P25009-378 BR-2.
    mapLibraries.push(
      <script key="mapboxSDK" src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`}></script>
    );
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id && analyticsAllowed) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin
      ></script>
    );

    analyticsLibraries.push(
      <script key="gtag dataLayer">
        {`
        // dataLayer, the gtag() shim and the Consent Mode v2 defaults are all
        // set up by the tag loader in public/index.html, which has to run
        // before any tag. These lines only recreate them defensively, then
        // configure the GA4 property.
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}

        gtag('config', '${googleAnalyticsId}', {
          cookie_flags: 'SameSite=None;Secure',
        });
        `}
      </script>
    );
  }

  if (plausibleDomains && isTrackingEnabled()) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin
      ></script>
    );
  }

  /* Publish the Mapbox token where both the on-demand map loader and the
   * geocoder can read it. It used to be parked on window.mapboxgl.accessToken,
   * which only worked because mapbox-gl was in the <head> of every page — the
   * very thing BR-2 asks us to stop doing. */
  useEffect(() => {
    if (isMapboxInUse) {
      setMapboxAccessToken(mapboxAccessToken);
    }
  }, [isMapboxInUse, mapboxAccessToken]);

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onMapLibLoaded = () => {
    // At this point we know that map library is loaded after it's dynamically included
    if (isMapboxInUse) {
      setMapboxAccessToken(mapboxAccessToken);
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s => s.id === GOOGLE_MAPS_SCRIPT_ID);
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
